import type { CategorySlug } from "@/api/types";

export interface TaxonomyNode {
  slug: string;
  label: string;
  children?: readonly TaxonomyNode[];
}

/**
 * Two levels below each top-level category, mirroring how Roblox creators
 * actually shop: `3d` is the anchor (what an asset *is* made of), the
 * systems/vehicles/scripts group is interactive behaviour, materials/plugins/maps
 * is world-building, and ui/vfx/audio/animations is presentation.
 *
 * Subcategory slugs are not unique across categories — `combat` lives under both
 * Systems and Animations — so every lookup takes the owning category.
 */
export const MARKETPLACE_TAXONOMY: Record<CategorySlug, readonly TaxonomyNode[]> = {
  "3d": [
    { slug: "characters", label: "Characters", children: [{ slug: "humanoid", label: "Humanoid" }, { slug: "creatures", label: "Creatures" }] },
    { slug: "props", label: "Props" },
    { slug: "environments", label: "Environments" },
    { slug: "buildings", label: "Buildings" },
  ],
  systems: [
    { slug: "combat", label: "Combat", children: [{ slug: "melee", label: "Melee" }, { slug: "ranged", label: "Ranged" }] },
    { slug: "progression", label: "Progression" },
    { slug: "multiplayer", label: "Multiplayer" },
    { slug: "economy", label: "Inventory & economy" },
  ],
  vehicles: [
    { slug: "cars", label: "Cars" },
    { slug: "aircraft", label: "Aircraft" },
    { slug: "boats", label: "Boats" },
    { slug: "rails", label: "Rails & trains" },
  ],
  scripts: [
    { slug: "utilities", label: "Utilities" },
    { slug: "ai", label: "AI & NPCs" },
    { slug: "game-logic", label: "Game logic" },
  ],
  materials: [
    { slug: "pbr", label: "PBR materials" },
    { slug: "textures", label: "Surface textures" },
    { slug: "decals", label: "Decals & images" },
    { slug: "skyboxes", label: "Skyboxes" },
  ],
  plugins: [
    { slug: "building", label: "Building" },
    { slug: "workflow", label: "Workflow" },
    { slug: "optimization", label: "Optimization" },
  ],
  maps: [
    { slug: "lobbies", label: "Lobbies" },
    { slug: "obbies", label: "Obbies" },
    { slug: "open-worlds", label: "Open worlds" },
  ],
  ui: [
    { slug: "huds", label: "HUDs", children: [{ slug: "health", label: "Health & status" }, { slug: "quest", label: "Quest tracking" }] },
    { slug: "menus", label: "Menus" },
    { slug: "inventory", label: "Inventory" },
    { slug: "icons", label: "Icons & fonts" },
  ],
  vfx: [
    { slug: "particles", label: "Particles" },
    { slug: "trails", label: "Trails" },
    { slug: "lighting", label: "Lighting" },
  ],
  audio: [
    { slug: "music", label: "Music" },
    { slug: "sound-effects", label: "Sound effects" },
    { slug: "voice", label: "Voice" },
  ],
  animations: [
    { slug: "movement", label: "Movement" },
    { slug: "combat", label: "Combat" },
    { slug: "emotes", label: "Emotes" },
  ],
};

export function findTaxonomyNode(category: CategorySlug, slug: string) {
  return MARKETPLACE_TAXONOMY[category].find((node) => node.slug === slug);
}

export function findTaxonomyLabel(category: CategorySlug, subcategory?: string, subscope?: string) {
  const subcategoryNode = subcategory ? findTaxonomyNode(category, subcategory) : undefined;
  if (!subcategoryNode) return undefined;
  if (!subscope) return subcategoryNode.label;
  return subcategoryNode.children?.find((node) => node.slug === subscope)?.label;
}

/** Deterministically scopes mock products without expanding the persisted product contract. */
export function productTaxonomyScope(productId: string, category: CategorySlug) {
  const subcategories = MARKETPLACE_TAXONOMY[category];
  const productNumber = Number(productId.replace("product-", "")) || 0;
  const subcategory = subcategories[productNumber % subcategories.length]!;
  const children = subcategory.children;
  const subscope = children?.[Math.floor(productNumber / subcategories.length) % children.length];
  return { subcategory: subcategory.slug, subscope: subscope?.slug };
}
